import React, { useState, useEffect } from 'react';
// Navigation uses window.location.href for reliability
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, deleteUser } from 'firebase/auth';
import { setDoc, doc, serverTimestamp, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { app, auth, db } from '../firebaseConfig';
import { getFunctions, httpsCallable } from "firebase/functions";
import { sendEmailVerification, sendPasswordResetEmail, fetchSignInMethodsForEmail } from 'firebase/auth';

const functions = getFunctions(app);
const createCustomer = httpsCallable(functions, 'createStripeCustomer');
const checkSitterApproval = httpsCallable(functions, 'checkSitterApproval');


const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('sitter'); // default to 'sitter'
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false); // toggle between sign-up and login
  // Navigation uses window.location.href for reliability
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // guards Signup/Login button from double-click
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [name, setName] = useState('');



  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // If user is authenticated and email is verified, redirect to their profile
      if (user && user.emailVerified) {
        window.location.href = `/profile/${user.uid}`;
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading…</p></div>;
  }

  const validateAddress = async (fullAddress) => {
    try {
      console.log('Validating address:', fullAddress);
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDCCNLE719tfQRXV3rqPZBZDBOXR-UGy9E'}`
      );
      
      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        // const result = data.results[0];
        // const addressComponents = result.address_components;
        // const requiredComponents = ['street_number', 'route', 'locality', 'administrative_area_level_1', 'country', 'postal_code'];
        // const providedComponents = addressComponents.map(component => component.types[0]);
        // const isFullAddress = requiredComponents.every(component => providedComponents.includes(component));
        const result = data.results[0];
        const components = result.address_components;
        const required = [
          'street_number',
          'route',
          'locality',
          'administrative_area_level_1',
          'country',
          'postal_code',
        ];

      const present = components.flatMap((c) => c.types);
      const isComplete = required.every((type) => present.includes(type));

        return isComplete ? result.formatted_address : null;
      } else {
        console.warn('Geocoding error:', data.status, data.error_message);
        return null;
      }
    } catch (error) {
      console.error('Error validating address:', error);
      return null;
    }
  };

  const handleSignUp = async () => {
    if (isSubmitting) return; // hard guard against double-click / accidental resubmit
    setIsSubmitting(true);
    setError('');

    // Trim everything up-front so nothing gets saved with stray whitespace.
    const nameTrim = name.trim();
    const streetTrim = streetAddress.trim();
    const cityTrim = city.trim();
    const stateTrim = state.trim();
    const zipTrim = zipCode.trim();
    const emailTrim = email.trim();

    try {
      if (!nameTrim) {
        setError('Please enter your full name.');
        return;
      }

      if (!streetTrim || !cityTrim || !stateTrim || !zipTrim) {
        setError('Please complete your full address.');
        return;
      }

      if (!emailTrim || !password) {
        setError('Email and password are required.');
        return;
      }

      // Build the exact address string that will be validated AND saved (was a
      // silent inconsistency before — one form used commas, the other spaces).
      const fullAddress = `${streetTrim} ${cityTrim}, ${stateTrim} ${zipTrim}`;
      if (isSignUp && !(await validateAddress(fullAddress))) {
        setError('Invalid address. Please enter a valid, full address.');
        return;
      }

      // Sitter signups are invitation-only: the email must be on the
      // approvedSitters allowlist (checked server-side) BEFORE any account
      // is created.
      if (userType === 'sitter') {
        try {
          const approvalRes = await checkSitterApproval({ email: emailTrim });
          if (!approvalRes.data?.approved) {
            setError('Sitter accounts are by invitation only. If you’d like to become a Handpicked Nanny sitter, email childcare@handpickednanny.com to get approved first.');
            return;
          }
        } catch (approvalErr) {
          console.error('Sitter approval check failed:', approvalErr);
          setError('We could not verify sitter approval right now. Please try again in a moment.');
          return;
        }
      }

      let user = null;
      let userDocCreated = false;
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, emailTrim, password);
        user = userCredential.user;

        const userData = {
          name: nameTrim,
          email: emailTrim,
          userType,
          address: fullAddress,
          streetAddress: streetTrim,
          city: cityTrim,
          state: stateTrim,
          zipCode: zipTrim,
          bio: '',
          profilePic: '',
          reviews: [],
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        };

        await setDoc(doc(db, 'users', user.uid), userData);
        userDocCreated = true;
        console.log('User document created successfully');

        // Brief delay for read-after-write consistency downstream.
        await new Promise(resolve => setTimeout(resolve, 500));

        // Create Stripe customer. If this fails, roll back so the user can retry
        // signup cleanly instead of being blocked by "email already exists".
        const res = await createCustomer({ userId: user.uid, email: emailTrim, name: nameTrim });
        if (!res.data.success) {
          throw new Error(res.data.message || 'Stripe customer creation failed');
        }

        await sendEmailVerification(user);
        console.log('Verification email sent to:', user.email);

        alert('A verification email has been sent to your address. Please check your spam if the email did not reach your inbox and "Report Not Spam" to ensure all future emails are directed to your primary inbox.');
        window.location.href = `/profile/${user.uid}`;
      } catch (innerErr) {
        // Roll back partial state so the user can try again with the same email.
        console.error('Signup failed after user creation, rolling back:', innerErr);
        let rollbackFailed = false;
        if (userDocCreated) {
          try { await deleteDoc(doc(db, 'users', user.uid)); }
          catch (e) { console.error('Rollback: failed to delete user doc:', e.message); rollbackFailed = true; }
        }
        if (user) {
          try { await deleteUser(user); }
          catch (e) { console.error('Rollback: failed to delete auth user:', e.message); rollbackFailed = true; }
        }
        if (rollbackFailed) {
          // Surface so the user knows their account is in an inconsistent state
          // and to contact support rather than silently retrying.
          setError('Signup partially failed and we could not fully clean up. Please contact support before trying again.');
          throw new Error('partial_signup_rollback_failed');
        }
        throw innerErr;
      }
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please log in or reset your password.');
      } else {
        setError('Error signing up: ' + (error.message || 'Unknown error'));
        console.error('Sign up error:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;
  
      if (!user.emailVerified) {
        await sendEmailVerification(user);
        setError('Please verify your email before logging in. A new verification email has been sent.');
        console.log('Email not verified, sent verification email to:', user.email);
        return; // stop further processing
      }
  
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userRef);
        const updates = {
          lastLoginAt: user.metadata?.lastSignInTime
            ? new Date(user.metadata.lastSignInTime)
            : serverTimestamp(),
        };

        if (!userSnapshot.exists()) {
          // In case the user document is missing, recreate minimal profile entry
          await setDoc(
            userRef,
            {
              email: user.email,
              name: user.displayName || '',
              userType: 'unknown',
              isActive: true,
              createdAt: user.metadata?.creationTime
                ? new Date(user.metadata.creationTime)
                : serverTimestamp(),
              updatedAt: serverTimestamp(),
              ...updates,
            },
            { merge: true }
          );
        } else {
          const userData = userSnapshot.data();
          if (!userData.createdAt && user.metadata?.creationTime) {
            updates.createdAt = new Date(user.metadata.creationTime);
          }
          if (!userData.updatedAt) {
            updates.updatedAt = serverTimestamp();
          }
          await updateDoc(userRef, updates);
        }
      } catch (metadataError) {
        console.error('Error updating login metadata:', metadataError);
      }

      window.location.href = `/profile/${user.uid}`;
    } catch (error) {
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
        setError('Incorrect email or password.');
      } else {
        setError('Login failed. Please try again.');
      }
      console.error('Login error:', error.code, error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (email) => {
    const emailTrim = (email || '').trim();
    if (!emailTrim) {
      alert("Please enter your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, emailTrim);
      alert("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.error("Error sending password reset email:", error);
  
      // Optional: Handle common error codes
      switch (error.code) {
        case "auth/invalid-email":
          alert("The email address is not valid.");
          break;
        case "auth/user-not-found":
          alert("No user found with that email.");
          break;
        default:
          alert("Something went wrong. Please try again.");
          break;
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSignUp) {
      handleSignUp();
    } else {
      handleLogin();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 px-4">
      <div className="w-full max-w-md p-6 sm:p-8 bg-white shadow-xl rounded-xl">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="Logo"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
            {isSignUp ? 'Sign Up' : 'Login'}
          </h2>
        </div>
  
        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          {!isSignUp && (
            <div className="text-right text-sm">
              <button
                type="button"
                onClick={() => {
                  if (!email) {
                    alert("Please enter your email first.");
                  } else {
                    handleForgotPassword(email);
                  }
                }}
                className="text-blue-600 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          )}
  
          {isSignUp && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">I am a:</label>
                <select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="sitter">Babysitter</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First and Last Name"
                  className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="123 Main St"
                  className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="State (e.g., GA)"
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="Zip"
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>
            </>
          )}
  
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 text-white font-semibold rounded-lg transition duration-300 ${isSubmitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
          >
            {isSubmitting ? (isSignUp ? 'Signing Up…' : 'Signing In…') : (isSignUp ? 'Sign Up' : 'Login')}
          </button>
  
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
        </form>
  
        <p className="mt-6 text-center text-sm text-gray-600">
          {isSignUp ? 'Already have an account?' : 'Don’t have an account?'}{' '}
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-blue-600 hover:underline font-medium"
          >
            {isSignUp ? 'Login' : 'Sign Up'}
          </button>
        </p>
      </div>
    </div>
  );
  
};

export default Auth;
